import { Box, Link, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import csvIcon from "../../../assets/images/csv.png";
import usePost from "../../../services/hooks/usePost";
import {
  apiEndpoints,
  ApiEnvironment,
  EEmptyFile,
  EErrorMessages,
} from "../../../shared/constants/api-constant";
import {
  AlertType,
  EGridStatus,
  ManageRoleTabs,
  ManageRoleValues,
  ManageSelectValue,
} from "../../../shared/constants/enums";
import { Button, Heading } from "../../../shared/ReusableComponents";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import LinkButton from "../../../shared/ReusableComponents/LinkButton";
import UploadSection from "../../../shared/ReusableComponents/UploadSection";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { toast } from "../../../store/toastSlice";

function AssignRoleUpload({
  heading,
  caption,
  selectedValue,
  selectedTab,
  onCancel,
}: any) {
  const [submitted, setSubmitted] = useState(false);
  const [isUpload, setIsUpload] = useState<boolean>(false);
  const [error, setError] = useState<any>("");
  const [csvFile, setCSVFile] = useState<any>({ fileName: "", data: null });
  const auth = useSelector((state: any) => state.login);
  const [gridData, setGridData] = useState<any>([]);
  const { data, loading: postLoading, postService } = usePost();
  const [typeName, setTypeName] = useState<any>("");
  const [headers, setHeaders] = useState<string[]>([]);

  const studentHeaders = [
    "Student/Tutor Code",
    "First Name",
    "Last Name",
    "Login ID",
    "Email ID",
    "Role Name",
    "Cohort Name",
  ];
  const assignRoleKeys = [
    "code",
    "firstName",
    "lastName",
    "loginId",
    "email",
    "roleName",
    "cohortName",
  ];
  const dispatch = useDispatch();

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!csvFile.data) {
      setError(EEmptyFile(selectedTab?.toLowerCase()));
      return;
    }
    if (csvFile.data.length < 1) {
      setError(EErrorMessages.NoDataFound);
      return;
    }
    setIsUpload(true);
    let roleKeys = assignRoleKeys;
    if (selectedValue === ManageSelectValue.STAFF) {
      roleKeys = roleKeys.filter((item: any) => item !== "cohortName");
    }
    const payload = csvFile.data.map((item: any) => {
      return roleKeys.reduce((acc: any, key: string, index: number) => {
        acc[key] = item[headers[index]];
        return acc;
      }, {});
    });
    dispatch(showLoader());
    postService(apiEndpoints.BULK_UPLOAD, {
      bulkJsonUser: JSON.stringify(payload),
    });
  };

  useEffect(() => {
    if (selectedValue === ManageSelectValue.STAFF) {
      const staffHeaders = studentHeaders
        .map((item) => (item === "Student/Tutor Code" ? "Staff Code" : item))
        .filter((item: any) => item !== "Cohort Name");
      setHeaders(staffHeaders);
    } else setHeaders(studentHeaders);
  }, [selectedValue]);

  useEffect(() => {
    !postLoading && dispatch(hideLoader());
  }, [postLoading]);

  useEffect(() => {
    if (data == null) return;
    let result = JSON.parse(data);
    const gridData = [
      {
        id: 1,
        fileName: csvFile.fileName,
        uploadedUser: result.length,
        success: result.filter(
          (item: any) => item.status === EGridStatus.SUCCESS
        ).length,
        failed: result.filter((item: any) => item.status === EGridStatus.FAILED)
          .length,
        uploadedBy: CommonFunctions.formatFirstAndLastName(
          auth?.userInfo?.userFirstName,
          auth?.userInfo?.userLastName
        ),
        result: result.map((item: any) => {
          const row: any = {
            [selectedValue === ManageSelectValue.STAFF
              ? "Staff Code"
              : "Student/Tutor Code"]: item.code,
            "First Name": item.firstName,
            "Last Name": item.lastName,
            "Login ID": item.userName,
            "Email ID": item.email,
            "Role Name": item.RoleName,
            "Cohort Name": item?.cohort,
            Status: item.status,
            "Reasons for Failure": item.reason,
          };
          if (selectedValue === ManageSelectValue.STAFF) {
            delete row["Cohort Name"];
          }
          return row;
        }),
      },
    ];
    setGridData(gridData);
    showMessage(gridData[0].success);
  }, [data]);

  useEffect(() => {
    const selectedType =
      selectedTab === ManageRoleTabs.STUDENT_TUTOR
        ? ManageRoleValues.STUDENT_TUTOR
        : selectedTab;
    setTypeName(selectedType);
  }, [selectedTab]);

  const showMessage = (assignedNumber = 0) => {
    const message = `${typeName} Roles have been Successfully Assigned to ${assignedNumber} ${
      typeName === ManageRoleValues.STUDENT_TUTOR
        ? typeName
        : `${typeName} Members`
    }.`;
    dispatch(
      toast({
        isOpen: true,
        message: message,
        type: AlertType.SUCCESS,
      })
    );
  };

  const downloadTemplate = () => {
    const fileName = `${
      selectedValue === ManageSelectValue.STAFF
        ? ManageSelectValue.STAFF
        : "studentTutor"
    }Template.csv`;

    const urlLink = `${process.env.PUBLIC_URL}/templates/${fileName}`;
    CommonFunctions.downloadFileFromLink(urlLink, fileName);
  };

  const downloadCSV = (fileName: string) => {
    if (gridData[0]?.result.length === 0) return;
    CommonFunctions.donwloadFileFromBlob(gridData[0].result, fileName);
  };

  const renderFile = (params: any) => {
    return (
      <Link
        data-testid="download-button"
        underline="always"
        sx={{ cursor: "pointer" }}
        onClick={() => downloadCSV(params.row.fileName)}
      >
        <Tooltip title={params.row.fileName}>
          <span>Download CSV</span>
        </Tooltip>
      </Link>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "fileName",
      headerName: "File Name",
    },
    {
      field: "uploadedUser",
      headerName: `No of ${typeName} Upload`,
    },
    {
      field: "success",
      headerName: "Success",
    },
    {
      field: "failed",
      headerName: "Failed",
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded by",
    },
    {
      field: "uploadResult",
      headerName: "Upload Result",
      renderCell: renderFile,
    },
  ];

  return (
    <React.Fragment>
      <Box className="upload-box">
        <Box className={`${isUpload ? "upload-opacity-box" : ""}`}>
          <Heading type="h5" text={heading} isBold={true} />
          <Heading isBold={true} type="body1" text={caption} />
        </Box>
        <Box className={`${isUpload ? "upload-opacity-box" : ""}`}>
          <Box sx={{ mt: 2 }} className="upload-box">
            <UploadSection
              inputFiles={{
                title: `Click to Upload Your ${typeName} List or Drag it Here.`,
                format: "CSV",
                size: 10,
                accept: ".csv",
                error: error,
              }}
              headers={headers}
              submitted={submitted}
              setError={() => setError("")}
              setData={(fileName, data) => setCSVFile({ fileName, data })}
            />
          </Box>
          <Box className="csv-template-box">
            <div className="divAlign justify-center">
              <img
                src={csvIcon}
                alt="csv"
                width={30}
                style={{ paddingRight: "15px" }}
              />
              <Heading type="h6" text="CSV Template" isBold={true} />
            </div>
            <div className="divAlign justify-center">
              <Heading text="Download Our Pre-Made, Easy-To-Use Template to Enter Your" />
              &nbsp;
              <Heading text={`"${typeName} List".`} isBold={true} />
            </div>
            <div className="divAlign justify-center">
              <span
                className="material-symbols-outlined"
                style={{ color: "var(--color-info-main)" }}
              >
                download
              </span>
              <LinkButton
                text="Download Template"
                onClick={downloadTemplate}
                style={{ fontSize: "16px" }}
              />
            </div>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "1rem",
            }}
          >
            <Button
              id="close"
              type="button"
              variant="outlined"
              fullWidth={false}
              text="Cancel"
              onClick={() => onCancel()}
              // className="secondaryBtn"
            />
            <Button
              id="submit"
              fullWidth={false}
              // textTransform="none"
              data-testid="submit-btn"
              className="ml-1"
              onClick={handleSubmit}
              text="Assign Role and Permission"
            />
          </Box>
        </Box>

        {isUpload && (
          <Grid className="data-Grid mt-1">
            <Heading isBold={true} text="Result:" type="h5" />
            <GridTable
              rows={gridData}
              columns={columns}
              isPaginationHide={true}
            />
          </Grid>
        )}
      </Box>
    </React.Fragment>
  );
}

export default AssignRoleUpload;
